import { useLessonFolders } from '@/hooks/useLessonFolders/useLessonFolders';
import { LessonFolder } from '@/models/lessonFolder';
import { useSession } from '@/providers/session/SessionProvider';
import FolderIcon from '@mui/icons-material/Folder';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Skeleton, Stack } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

const FOLDER_PAGE_SIZE = 12;

const FONT_SIZE = 16;

interface Props {
    open: boolean;
    title: string;
    onClose: () => void;
    onUpdate: (value: LessonFolder | null) => Promise<void>;
}

export const LessonMoveDialog: React.FC<Props> = ({ open, onClose, onUpdate, title }) => {
    const { session } = useSession();
    const { t } = useTranslation();

    // ui state
    const [saving, setSaving] = React.useState(false);
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [folders, setFolders] = useState<LessonFolder[]>([]);

    // query
    const folderFilter = React.useMemo(() => {
        if (!session?.user?.teacherProfile?.id) return [];
        return { teacher: { id: { equals: session?.user?.teacherProfile?.id } } };
    }, [session?.user?.teacherProfile?.id]);
    const { folders: fetchedFolders, loading, total: totalFoldersCount } = useLessonFolders(folderFilter, { index: page, size: FOLDER_PAGE_SIZE });

    const hasMore = folders && totalFoldersCount ? folders.length < totalFoldersCount : false;
    const remainingFolderCount = totalFoldersCount ? totalFoldersCount - folders.length : 0;

    const { ref: loadMoreRef } = useInView({
        threshold: 0,
        onChange: (inView) => {
            if (inView && !loading && hasMore) {
                setPage((prev) => prev + 1);
            }
        },
    });

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!selectedFolder) return;

        setSaving(true);
        const folder = folders.find((f) => f.id === selectedFolder);
        await onUpdate(selectedFolder === 'root' || !folder ? null : folder);
        setSaving(false);

        onClose();
    };

    const handleSelectedItemsChange = (_event: React.SyntheticEvent | null, id: string | null) => {
        setSelectedFolder(id);
    };

    useEffect(() => {
        if (fetchedFolders) {
            setFolders((prev) => [...prev, ...fetchedFolders]);
        }
    }, [fetchedFolders]);

    return (
        <Dialog fullWidth open={open} onClose={() => onClose()} disableRestoreFocus>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>
                    {t('lesson.actions.move')} "{title}"
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Box sx={{ height: 300, overflowY: 'scroll' }}>
                            <SimpleTreeView
                                slots={{
                                    endIcon: FolderIcon,
                                }}
                                sx={{ pb: 1 }}
                                expandedItems={['root']}
                                selectedItems={selectedFolder}
                                onSelectedItemsChange={handleSelectedItemsChange}
                            >
                                <TreeItem
                                    itemId='root'
                                    label={t('lesson.header')}
                                    slotProps={{
                                        label: {
                                            style: { fontSize: FONT_SIZE, fontWeight: 'bold' },
                                        },
                                    }}
                                >
                                    {folders &&
                                        folders.map((folder, index) => (
                                            <TreeItem
                                                key={folder.id}
                                                itemId={folder.id}
                                                label={folder.name}
                                                slotProps={{
                                                    label: {
                                                        style: { fontSize: FONT_SIZE },
                                                    },
                                                }}
                                                ref={hasMore && !loading && index === folders.length - 1 ? loadMoreRef : undefined}
                                            />
                                        ))}
                                    {loading &&
                                        Array.from(Array(remainingFolderCount < FOLDER_PAGE_SIZE ? remainingFolderCount : FOLDER_PAGE_SIZE)).map((_, index) => (
                                            <TreeItem
                                                key={index}
                                                itemId={`skeleton-${index}`}
                                                label={<Skeleton variant='text' width={160} />}
                                                slotProps={{
                                                    label: {
                                                        style: { fontSize: FONT_SIZE },
                                                    },
                                                }}
                                            />
                                        ))}
                                </TreeItem>
                            </SimpleTreeView>
                        </Box>
                    </Box>
                    <Divider />
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose();
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} variant='contained' type='submit' loadingIndicator={<CircularProgress size={16} />} disabled={!selectedFolder}>
                        {t('lesson.actions.move')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
};
